/**
 * Keystroke/paste refusal for antd `InputNumber`. rc-input-number holds whatever was typed as its
 * **own** display string and, when the parser cannot read it, merely declines to update the value
 * — so `abc` stayed in the box until a blur flushed it (QA 119717, UAT 120963). A `parser` never
 * touches the display, so the characters have to be refused before they land.
 */
export type NumericInputOptions = {
  /** A single `.` may be typed. */
  decimal: boolean;
  /** A single leading `-` may be typed. */
  negative: boolean;
  /** A ceiling the resulting value may not exceed — refused at the keystroke like any other bad
   *  character, so an over-cap count never reaches the API (ADO 121208). */
  max?: number;
};

/** The input's text either side of its selection — what a key or paste would land between. */
export type SelectionContext = { before: string; after: string };

type KeyLike = { key: string; ctrlKey: boolean; metaKey: boolean; altKey: boolean };

function isNumericText(text: string, options: NumericInputOptions): boolean {
  const sign = options.negative ? '-?' : '';
  const fraction = options.decimal ? '(\\.\\d*)?' : '';
  if (!new RegExp(`^${sign}\\d*${fraction}$`).test(text)) return false;
  return options.max === undefined || !(Number(text) > options.max);
}

/**
 * A printable key is one character long; `Backspace`, `Delete`, `Tab`, `Home` and the arrows are
 * all longer, and a modifier combination is copy/paste/select-all rather than input.
 */
export function isRefusedNumericKey(
  event: KeyLike,
  options: NumericInputOptions,
  { before, after }: SelectionContext = { before: '', after: '' },
): boolean {
  if (event.ctrlKey || event.metaKey || event.altKey) return false;
  if (event.key.length !== 1) return false;
  return !isNumericText(`${before}${event.key}${after}`, options);
}

/** Whether pasting `text` over the selection still leaves a number this input may hold. */
export function isRefusedNumericPaste(
  text: string,
  options: NumericInputOptions,
  { before, after }: SelectionContext = { before: '', after: '' },
): boolean {
  return !isNumericText(`${before}${text}${after}`, options);
}

/** A pasted value's numeric residue: digits, a leading `-` and the first `.`, each only where the
 *  options allow them — so `12.34abc` becomes `12.34` rather than being refused outright. */
export function stripToNumericText(text: string, options: NumericInputOptions): string {
  const negative = options.negative && text.trimStart().startsWith('-');
  const [whole = '', ...fraction] = text.replace(/[^\d.]/g, '').split('.');
  const decimals = options.decimal && fraction.length > 0 ? `.${fraction.join('')}` : '';
  return `${negative ? '-' : ''}${whole}${decimals}`;
}

export function selectionContext(input: HTMLInputElement): SelectionContext {
  const { value, selectionStart, selectionEnd } = input;
  if (selectionStart === null || selectionEnd === null) return { before: value, after: '' };
  return { before: value.slice(0, selectionStart), after: value.slice(selectionEnd) };
}
